import { CtuFieldName, DocumentType } from '@/api/generated-types';

// errorRoutable keys must equal `${CtuFieldName}` (asserted below). See CONTEXT.md "Field policy".
type CtuFieldDef = Readonly<{
  /** Path from the CTU root, not the form root. */
  path: string;
  label: string;
  hash?: boolean;
  errorRoutable?: boolean;
  /** Mirrors CtuFieldDefaults.CanDisable on BE. */
  canDisable?: boolean;
  hint?: string;
  /** Doc types this field applies to; omit = shared. Mirrors DocumentVocabulary.DcmOnlyCtu on BE. */
  documentType?: DocumentType;
}>;

export const CTU_FIELD_REGISTRY = {
  containerNumber: {
    path: 'containerInformation.containerNumber',
    label: 'Container Number',
    hash: true,
    errorRoutable: true,
    canDisable: true,
    hint: 'ISO 6346',
  },
  isoCode: {
    path: 'containerInformation.isoCode',
    label: 'Container Type',
    hash: true,
    errorRoutable: true,
    canDisable: true,
    hint: 'ISO 6346',
  },
  size: {
    path: 'containerInformation.size',
    label: 'Container Size',
    hash: true,
    errorRoutable: true,
    canDisable: true,
    hint: 'Free-text size descriptor',
  },
  stowagePosition: {
    path: 'stowagePosition',
    label: 'Stowage Position',
    hash: true,
    errorRoutable: true,
    hint: 'Bay-row-tier code',
    documentType: DocumentType.Dcm,
  },
  unitType: {
    path: 'unitType',
    label: 'Unit Type',
    hash: true,
  },
  sealNumber: { path: 'containerInformation.sealNumber', label: 'Seal Number' },
  // Hashed because vNext validates both against the cargo sums, so an edit to either can turn a clean
  // container into a 400 on its own — without them a weight-only changeset looks unchanged.
  grossWeight: { path: 'containerInformation.grossWeight', label: 'Gross Weight', hash: true },
  tareMassKg: { path: 'containerInformation.tareMassKg', label: 'Tare Mass (kg)', hash: true },
  volumeInM3: { path: 'containerInformation.volumeInM3', label: 'Volume (m³)' },
} as const satisfies Readonly<Record<string, CtuFieldDef>>;

export type CtuRegistryKey = keyof typeof CTU_FIELD_REGISTRY;

// Drift gate: errorRoutable registry keys MUST equal `${CtuFieldName}`.
// Adding a CtuFieldName value or an errorRoutable entry without the other
// side fails compile; the unmatched value surfaces in the error message.
type _ErrorRoutableKeys = {
  [K in keyof typeof CTU_FIELD_REGISTRY]: (typeof CTU_FIELD_REGISTRY)[K] extends {
    errorRoutable: true;
  }
    ? K
    : never;
}[keyof typeof CTU_FIELD_REGISTRY];

type _AssertSetsEqual<A extends string, B extends string> = [Exclude<A, B>] extends [never]
  ? [Exclude<B, A>] extends [never]
    ? true
    : { CtuFieldName_missing_for: Exclude<B, A> }
  : { CTU_FIELD_REGISTRY_extra_errorRoutable: Exclude<A, B> };

const _ctuCoverage: _AssertSetsEqual<_ErrorRoutableKeys, `${CtuFieldName}`> = true;

export interface CtuFieldConfig {
  label: string;
  canDisable: boolean;
  hint?: string;
  documentType?: DocumentType;
}

export const CTU_FIELD_CONFIG: Record<CtuFieldName, CtuFieldConfig> = Object.fromEntries(
  (Object.values(CtuFieldName) as CtuFieldName[]).map((v) => {
    const e = (CTU_FIELD_REGISTRY as Readonly<Record<string, CtuFieldDef>>)[v];
    return [
      v,
      { label: e.label, canDisable: e.canDisable ?? false, hint: e.hint, documentType: e.documentType },
    ];
  })
) as Record<CtuFieldName, CtuFieldConfig>;

// Literal-keyed (not `Record<string,…>`) so `keyof typeof` narrows to
// `keyof ContainerInformationDto` in rerunDiff's typed iteration.
export const CTU_CONTAINER_FIELD_LABELS = {
  containerNumber: CTU_FIELD_REGISTRY.containerNumber.label,
  sealNumber: CTU_FIELD_REGISTRY.sealNumber.label,
  isoCode: CTU_FIELD_REGISTRY.isoCode.label,
  size: CTU_FIELD_REGISTRY.size.label,
  grossWeight: CTU_FIELD_REGISTRY.grossWeight.label,
  tareMassKg: CTU_FIELD_REGISTRY.tareMassKg.label,
} as const;

export function getCtuFieldLabel(field: string): string {
  const entry = (CTU_FIELD_REGISTRY as Record<string, { label: string }>)[field];
  return entry?.label ?? field;
}

// Most CTU fields live under containerInformation, but some are top-level (e.g. stowagePosition),
// so the path comes from the registry, not a hardcoded prefix.
export function getCtuFieldPathSuffix(field: string): string {
  const entry = (CTU_FIELD_REGISTRY as Record<string, { path: string }>)[field];
  return entry?.path ?? `containerInformation.${field}`;
}
